import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Send, FileEdit, X, Copy, Check, UserPlus, Users, Trash2, Search, Filter } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function JobMatches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [introModal, setIntroModal] = useState(null); // { job, candidates }
  const [introDraft, setIntroDraft] = useState(null); // { connection, draft, job, channel, loading }
  const [filter, setFilter] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState("all");
  const [minScore, setMinScore] = useState(0);
  const [keywords, setKeywords] = useState([]);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/jobs/matches?limit=200"),
      api.get("/jobs/keywords"),
    ]).then(([m, k]) => {
      setJobs(m.data);
      setKeywords(k.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Delete ALL saved jobs? Applications you already tracked stay safe.")) return;
    await api.delete("/jobs/clear?confirm=true");
    loadAll();
  };

  const clearKeyword = async (kw) => {
    if (!window.confirm(`Delete all jobs from the "${kw}" search?`)) return;
    await api.delete(`/jobs/clear?keyword=${encodeURIComponent(kw)}`);
    setSelectedKeyword("all");
    loadAll();
  };

  const filteredJobs = useMemo(() => {
    const f = filter.toLowerCase().trim();
    return jobs.filter((j) => {
      if (selectedKeyword !== "all" && (j.search_keyword || "unknown") !== selectedKeyword) return false;
      if (minScore && (j.match_score || 0) < minScore) return false;
      if (!f) return true;
      const hay = `${j.title || ""} ${j.company || ""} ${j.location || ""} ${j.description || ""}`.toLowerCase();
      return hay.includes(f);
    });
  }, [jobs, filter, selectedKeyword, minScore]);

  const deepMatch = async (jobId) => {
    setAnalyzing(jobId);
    try {
      const r = await api.post(`/jobs/${jobId}/deep-match`);
      setJobs((js) => js.map((j) => j.job_id === jobId ? { ...j, match_score: r.data.match_score || j.match_score, match_detail: r.data } : j));
    } catch (e) {
      alert(e.response?.data?.detail || "Deep analyze failed");
    } finally { setAnalyzing(null); }
  };

  const goTailor = (job) => {
    const parts = [];
    if (job.title) parts.push(`Job Title: ${job.title}`);
    if (job.company) parts.push(`Company: ${job.company}`);
    if (job.location) parts.push(`Location: ${job.location}`);
    const jd = `${parts.join("\n")}\n\n${job.description || ""}`.trim();
    navigate("/tailored-resume", { state: { jd, jobTitle: job.title, company: job.company } });
  };

  const apply = async (job) => {
    // Open the job's real apply page on its original site (LinkedIn/Indeed/Glassdoor/Naukri)
    if (job.url) window.open(job.url, "_blank", "noopener");
    try {
      await api.post("/applications", { job_id: job.job_id, status: "Applied" });
    } catch (e) {
      if (e.response?.status !== 400) alert("Failed to track application");
    }
  };

  const openIntro = async (job) => {
    try {
      const r = await api.get(`/jobs/${job.job_id}/referrals`);
      setIntroModal({ job, candidates: r.data.candidates });
    } catch (e) { alert("Failed to load referrals"); }
  };

  const draftIntro = async (connection, channel = "linkedin") => {
    if (!introModal) return;
    setIntroDraft({ connection, job: introModal.job, channel, loading: true });
    try {
      const r = await api.post("/warm-intro/draft", {
        job_id: introModal.job.job_id,
        connection_id: connection.connection_id,
        channel,
      });
      setIntroDraft({ connection: r.data.connection, draft: r.data.draft, job: introModal.job, channel, loading: false });
    } catch (e) {
      setIntroDraft(null);
      alert(e.response?.data?.detail || "Draft failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="job-matches-page">
      {user && !(user.profile?.skills?.length) && (
        <div className="jr-card border border-amber-500/40 text-amber-200 text-sm flex items-center justify-between gap-3" data-testid="no-profile-banner">
          <span>Match scores are generic right now — upload your resume so jobs are scored against your real skills.</span>
          <Link to="/upload-resume" className="jr-btn-primary text-xs py-2 px-3 shrink-0" data-testid="upload-resume-cta">Upload Resume</Link>
        </div>
      )}
      <div className="jr-card" data-testid="matches-toolbar">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter title / company / description (e.g. 'Gen AI', 'LLM', 'Meta')"
              className="jr-input !pl-10" data-testid="filter-input" />
          </div>
          <select value={selectedKeyword} onChange={(e) => setSelectedKeyword(e.target.value)}
            className="jr-input lg:w-64" data-testid="keyword-filter">
            <option value="all" className="bg-[#161635]">{`All searches (${jobs.length})`}</option>
            {keywords.map((k) => (
              <option key={k.keyword} value={k.keyword} className="bg-[#161635]">
                {`${k.keyword} (${k.count})`}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 shrink-0">Min match</span>
            <input type="range" min="0" max="95" step="5" value={minScore}
              onChange={(e) => setMinScore(parseInt(e.target.value))}
              className="w-24" data-testid="min-score" />
            <span className="text-xs text-violet-200 w-8">{minScore}%</span>
          </div>
          {selectedKeyword !== "all" ? (
            <button onClick={() => clearKeyword(selectedKeyword)} className="jr-btn-ghost text-sm text-rose-300" data-testid="clear-keyword-btn">
              <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Clear this search
            </button>
          ) : (
            <button onClick={clearAll} className="jr-btn-ghost text-sm text-rose-300" data-testid="clear-all-btn">
              <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Clear all
            </button>
          )}
        </div>
        <div className="text-xs text-zinc-400 mt-3">
          Showing <strong className="text-white">{filteredJobs.length}</strong> of {jobs.length} jobs
          {selectedKeyword !== "all" && <> for search &quot;<span className="text-violet-200">{selectedKeyword}</span>&quot;</>}
        </div>
      </div>

      {loading && <div className="text-zinc-400">Loading matches...</div>}
      {!loading && jobs.length === 0 && <div className="jr-card text-zinc-400 text-center py-8">
        No matches yet. Head to <strong className="text-violet-300">Job Search</strong> and search for the roles you actually want (e.g., &quot;Gen AI Engineer&quot;, &quot;LLM Engineer&quot;).
      </div>}
      {!loading && jobs.length > 0 && filteredJobs.length === 0 && (
        <div className="jr-card text-zinc-400 text-center py-8">No jobs match these filters. Try lowering the match threshold or clearing the search filter.</div>
      )}
      {filteredJobs.map((j) => (
        <div key={j.job_id} className="jr-card" data-testid="match-card">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-white/10 grid place-items-center text-white font-bold shrink-0">
                {(j.company || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{j.title}</div>
                <div className="text-xs text-zinc-400 truncate">{j.company} • {j.location} • <span className="text-violet-300">{j.source}</span></div>
              </div>
            </div>
            <div className="flex flex-col items-stretch md:items-end gap-2 shrink-0">
              <div className="text-2xl font-bold text-emerald-300 md:text-right">{j.match_score}%</div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => deepMatch(j.job_id)} className="jr-btn-ghost text-xs py-1.5 px-2.5" data-testid="deep-match-btn">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  {analyzing === j.job_id ? "Analyzing..." : "Deep Analyze"}
                </button>
                <button onClick={() => goTailor(j)} className="jr-btn-ghost text-xs py-1.5 px-2.5" data-testid="tailor-for-job-btn" title="Opens the Tailored Resume tab with this job's description pre-filled">
                  <FileEdit className="w-3 h-3 inline mr-1" />
                  Tailor for this Job
                </button>
                {j.referral_count > 0 && (
                  <button onClick={() => openIntro(j)}
                    className="text-xs py-1.5 px-2.5 rounded-lg font-medium border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 transition"
                    data-testid="warm-intro-btn"
                    title={`${j.referral_count} connection${j.referral_count > 1 ? "s" : ""} at ${j.company}`}>
                    <UserPlus className="w-3 h-3 inline mr-1" />
                    Warm Intro ({j.referral_count})
                  </button>
                )}
                <button onClick={() => apply(j)} className="jr-btn-primary text-xs py-1.5 px-2.5" data-testid="apply-btn" title="Opens the job's apply page and tracks it">
                  <Send className="w-3 h-3 inline mr-1" /> Apply
                </button>
              </div>
            </div>
          </div>
          {j.match_detail && (
            <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-zinc-400 text-xs mb-1">Strengths</div>
                <ul className="list-disc list-inside text-emerald-200 text-xs space-y-0.5">
                  {(j.match_detail.strengths || []).slice(0, 4).map((s, i) => <li key={`s-${i}-${s.slice(0,16)}`}>{s}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-zinc-400 text-xs mb-1">Gaps</div>
                <ul className="list-disc list-inside text-amber-200 text-xs space-y-0.5">
                  {(j.match_detail.gaps || []).slice(0, 4).map((s, i) => <li key={`g-${i}-${s.slice(0,16)}`}>{s}</li>)}
                </ul>
              </div>
              {j.match_detail.recommendation && (
                <div className="md:col-span-2 text-xs text-violet-200 bg-violet-500/10 p-3 rounded-lg">
                  <strong>AI Recommendation:</strong> {j.match_detail.recommendation}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {introModal && !introDraft && (
        <IntroPickerModal
          data={introModal}
          onPick={draftIntro}
          onClose={() => setIntroModal(null)}
        />
      )}
      {introDraft && (
        <IntroDraftModal
          data={introDraft}
          onBack={() => setIntroDraft(null)}
          onClose={() => { setIntroDraft(null); setIntroModal(null); }}
          onChannelChange={(ch) => draftIntro(introDraft.connection, ch)}
        />
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="jr-btn-ghost text-xs py-1.5"
      data-testid="copy-btn"
    >
      {copied ? <><Check className="w-3 h-3 inline mr-1" /> Copied</> : <><Copy className="w-3 h-3 inline mr-1" /> Copy</>}
    </button>
  );
}

function IntroPickerModal({ data, onPick, onClose }) {
  const { job, candidates } = data;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4 jr-fade-in" data-testid="intro-picker-modal">
      <div className="jr-card w-full max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-300" />
            <div className="font-bold text-white">Warm Intro Candidates</div>
            <span className="jr-pill bg-cyan-500/20 text-cyan-200 text-[10px]">{candidates.length} found</span>
          </div>
          <button onClick={onClose} className="jr-btn-ghost p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-zinc-400 mb-3">at <strong className="text-white">{job.company}</strong> for {job.title}. Pick someone — Claude will draft a referral request.</div>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {candidates.map((c) => (
            <button
              key={c.connection_id}
              onClick={() => onPick(c)}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] hover:bg-cyan-500/10 border border-transparent hover:border-cyan-500/30 transition text-left"
              data-testid="pick-connection-btn"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 grid place-items-center text-white font-bold text-sm shrink-0">
                {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">{c.name}</div>
                <div className="text-xs text-zinc-400">{c.role || "—"} @ {c.company}</div>
              </div>
              <Sparkles className="w-4 h-4 text-violet-300" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function IntroDraftModal({ data, onBack, onClose, onChannelChange }) {
  const { connection, draft, job, channel, loading } = data;
  const text = draft ? (draft.channel === "email"
    ? `Subject: ${draft.subject || ""}\n\n${draft.message || ""}`
    : draft.message || "") : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4 jr-fade-in" data-testid="intro-draft-modal">
      <div className="jr-card w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3 sticky top-0 bg-[#161635] pb-2">
          <div>
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-cyan-300" />
              <div className="font-bold text-white">Intro Draft</div>
              <span className="jr-pill bg-cyan-500/20 text-cyan-200 text-[10px]">Warm-Intro Agent</span>
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              To <strong className="text-white">{connection.name}</strong> ({connection.role}) at {connection.company} • re: {job.title}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!loading && draft && <CopyButton text={text} />}
            <button onClick={onClose} className="jr-btn-ghost p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => onChannelChange("linkedin")} disabled={loading}
            className={`text-xs py-1.5 px-3 rounded-lg ${channel === "linkedin" ? "bg-violet-500/25 text-violet-100 border border-violet-500/40" : "bg-white/5 text-zinc-400"}`}
            data-testid="channel-linkedin">LinkedIn DM</button>
          <button onClick={() => onChannelChange("email")} disabled={loading}
            className={`text-xs py-1.5 px-3 rounded-lg ${channel === "email" ? "bg-violet-500/25 text-violet-100 border border-violet-500/40" : "bg-white/5 text-zinc-400"}`}
            data-testid="channel-email">Email</button>
          <button onClick={onBack} className="ml-auto jr-btn-ghost text-xs py-1.5">← Pick someone else</button>
        </div>

        {loading && <div className="text-sm text-violet-300 jr-pulse">Drafting your message with Claude…</div>}

        {!loading && draft && (
          <>
            {draft.channel === "email" && draft.subject && (
              <div className="mb-3">
                <div className="text-xs text-zinc-400 mb-1">Subject</div>
                <div className="text-white font-semibold">{draft.subject}</div>
              </div>
            )}
            <div className="bg-white/[0.04] p-4 rounded-lg whitespace-pre-wrap text-zinc-100 text-sm leading-relaxed">
              {draft.message}
            </div>
            {draft.tips?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Tips before sending</div>
                <ul className="text-xs text-amber-200 space-y-1 list-disc list-inside">
                  {draft.tips.map((t, i) => <li key={`tip-${i}-${(t||'').slice(0,20)}`}>{t}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
